import React, {useState, useEffect, useMemo, useCallback} from 'react';
import SMCModal from '../../components/core/SMCModal';
import Api from '../../api/SMPFetchHelper';
import {useSMCErrorHandler} from '../../utils/errorUtils';
import SMCModalBody from '../../components/core/SMCModal/SMCModalBody'
import {useTranslation} from 'react-i18next';
import SMCModalButtonContainer from '../../components/core/SMCModal/SMCModalButtonContainer';
import SMCLabel from '../../components/Simple/SMCLabel';
import {SMCSelect, SMCOption} from '../../components/Simple/SMCSelect';
import SMCButton from '../../components/Simple/SMCButton';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {SessionDataGetSchema} from '../../api/DBSchema/SessionData';
import {ObjectTypes} from '../../api/consts';

function GatewayMoveToPlanModal({
    isOpen,
    onRequestClose,
    onSuccess,
    selectedGateways
}) {
    const { t } = useTranslation();
    const { getErrorMessage } = useSMCErrorHandler();

    const [step, setStep] = useState('CHOOSE_PLAN');
    const [selectedPlan, setSelectedPlan] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState(null);
    const [availablePlans, setAvailablePlans] = useState([]);
    const [gatewaysMovedSuccessfully, setGatewaysMovedSuccessfully] = useState(null);
    const [gatewaysNotMovedSuccessfully, setGatewaysNotMovedSuccessfully] = useState(null);
    const [gatewaysMovedUnsuccessfully, setGatewaysMovedUnsuccessfully] = useState(null);

    const onFinish = useCallback(() => {
        onSuccess && onSuccess();
    }, [onSuccess]);

    // On each new open, reset values
    useEffect(() => {
        if (isOpen) {
            setStep('CHOOSE_PLAN')
            setSelectedPlan(availablePlans && availablePlans[0] ? availablePlans[0] : '');
            setErrorMessage(null);
        }
    }, [isOpen, availablePlans]);

    useEffect(() => {
        setIsLoading(true);
        Api.getSessionData({
            attributes: [SessionDataGetSchema.availablePlans]
        })
        .then(resp => {
            if (Array.isArray(resp.data[SessionDataGetSchema.availablePlans])) {
                setAvailablePlans(resp.data[SessionDataGetSchema.availablePlans]);
            } else {
                setErrorMessage(t('SMP.GATEWAYS.CHANGE_PLAN.ERROR.FETCHING_ALL'));
            }
        })
        .catch(e => {
            const errorMessage = getErrorMessage(e);
            setErrorMessage(errorMessage);
        })
        .finally(() => {
            setIsLoading(false);
        })
    }, [getErrorMessage, t]);

    // const handleChoosePlan = useCallback(() => {
    //     const planCounter = {};
    //     if (selectedGateways) {
    //         selectedGateways.forEach(gateway => {
    //             const planId = gateway[GatewayGetListSchema.planId];
    //             if (planCounter[planId]) {
    //                 planCounter[planId]++;
    //             } else {
    //                 planCounter[planId] = 1;
    //             }
    //         })
    //     }
    //     setFromPlanCounter(planCounter);
    //     setStep('CONFIRM');
    // }, [selectedGateways]);

    const handleChoosePlan = useCallback(() => {
        if (window.confirm('Clicking OK will move all selected gateways to plan "'+selectedPlan+'"\n(This can not be undone)')) {
            setIsLoading(true);
            Api.doAction(ObjectTypes.GATEWAY, 'move-to-plan', selectedGateways, {planId: selectedPlan})
                .then(resp => {
                    const results = resp.data.results;
                    const successfulCount = results.filter(data => data.success && data.errorCode !== 'GATEWAY_ALREADY_IN_PLAN').length;
                    const unsuccessfulCount = results.filter(data => !data.success).length;
                    const gatewaysNotMoved = results.filter(data => data.errorCode === 'GATEWAY_ALREADY_IN_PLAN').length;
                    setGatewaysMovedSuccessfully(successfulCount);
                    setGatewaysMovedUnsuccessfully(unsuccessfulCount);
                    setGatewaysNotMovedSuccessfully(gatewaysNotMoved);
                })
                .catch(e => {
                    const errorMessage = getErrorMessage(e);
                    setErrorMessage(errorMessage);
                })
                .finally(() => {
                    setIsLoading(false);
                })
            setStep('DONE');
        }
    }, [selectedGateways, selectedPlan, getErrorMessage]);

    const body = useMemo(() => {
        if (errorMessage) {
            return (
                <>
                <SMCLabel bold>{t('SMC_GEN_ERROR')}</SMCLabel>
                <SMCLabel>{errorMessage}</SMCLabel>
                </>
            );
        }
        if (step === 'CHOOSE_PLAN') {
            return (
                <>
                <SMCLabel bold>{t('SMC_MOVETOPLAN_HEAD')}</SMCLabel>
                <SMCSelect value={selectedPlan} onChange={e => setSelectedPlan(e.target.value)}>
                    {
                        availablePlans.map(plan => (
                            <SMCOption value={plan} key={plan}>{plan}</SMCOption>
                        ))
                    }
                </SMCSelect>
                </>
            );
        }
        // if (step === 'CONFIRM') {
        //     return (
        //         <>
        //         <SMCLabel bold>{t('SMC_GEN_CONFIRM')}</SMCLabel>
        //         {
        //             Object.keys(fromPlanCounter).map(planName => (
        //                 <SMCLabel>
        //                     Moving {fromPlanCounter} gateways from plan <b>{planName}</b> to plan <b>{plan}</b> {planName === plan && '(Not moved)'}
        //                 </SMCLabel>
        //             ))
        //         }
        //         </>
        //     );
        // }
        if (step === 'DONE') {
            return (
                <>
                <SMCLabel bold>{t('SMC_GEN_DONE')}</SMCLabel>
                {
                    !!gatewaysMovedSuccessfully && (
                        <SMCLabel>Moved <b>{gatewaysMovedSuccessfully}</b> gateways to plan <b>{selectedPlan}</b></SMCLabel>
                    )
                }
                {
                    !!gatewaysNotMovedSuccessfully && (
                        <SMCLabel><b>{gatewaysNotMovedSuccessfully}</b> gateways not moved (were already in plan <b>{selectedPlan}</b>)</SMCLabel>
                    )
                }
                {
                    !!gatewaysMovedUnsuccessfully && (
                        <SMCLabel><b>{gatewaysMovedUnsuccessfully}</b> gateways were not able to move to plan <b>{selectedPlan}</b></SMCLabel>
                    )
                }
                </>
            )
        }
    }, [availablePlans, gatewaysMovedSuccessfully, gatewaysNotMovedSuccessfully, gatewaysMovedUnsuccessfully, selectedPlan, errorMessage, step, t]);

    const buttons = useMemo(() => {
        if (step === 'CHOOSE_PLAN') {
            return (
                <>
                <SMCButton onClick={handleChoosePlan}>
                    {t('SMC_GEN_WIZNEXT')}
                    <FontAwesomeIcon style={{ marginTop: '3px', marginLeft: '5px' }} color="grey" icon="arrow-circle-right" />
                </SMCButton>
                <SMCButton onClick={onRequestClose}>
                    <FontAwesomeIcon style={{ marginTop: '3px', marginRight: '5px' }} color="grey" icon="times" />
                    {t('SMC_GEN_CANCEL')}
                </SMCButton>
                </>
            );
        }
        // if (step === 'CONFIRM') {
        //     return (
        //         <>
        //         <SMCButton onClick={onFinish}>
        //             {t('SMC_GEN_CONFIRM')}
        //             <FontAwesomeIcon style={{ marginTop: '3px', marginLeft: '5px' }} color="grey" icon="arrow-circle-right" />
        //         </SMCButton>
        //         <SMCButton onClick={onRequestClose}>
        //             <FontAwesomeIcon style={{ marginTop: '3px', marginRight: '5px' }} color="grey" icon="times" />
        //             {t('SMC_GEN_CANCEL')}
        //         </SMCButton>
        //         </>
        //     );
        // }
        if (step === 'DONE') {
            return (
                <>
                <SMCButton onClick={onFinish}>
                    {t('SMC_GEN_FINISH')}
                    <FontAwesomeIcon style={{ marginTop: '3px', marginLeft: '5px' }} color="green" icon="check" />
                </SMCButton>
                </>
            );
        }
    }, [handleChoosePlan, onFinish, onRequestClose, step, t]);

    return (
        <SMCModal
            isOpen={isOpen}
            width={700}
            height={550}
            title={t('SMC_MOVETOPLAN_TITLE')}
            onRequestClose={onRequestClose}
            loading={isLoading}
        >
            <SMCModalBody>
                { body }
            </SMCModalBody>
            <SMCModalButtonContainer>
                { buttons }
            </SMCModalButtonContainer>
        </SMCModal>

    );
}

export default GatewayMoveToPlanModal;